import { useEffect, useRef, useState } from "react";
import { api } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Camera, Upload, ImageIcon, X } from "lucide-react";
import { IMAGE_CATEGORIES } from "./helpers";

export const ImageGalleryCard = ({ orderId, onRequestUpload }) => {
  const [images, setImages] = useState([]);
  const fileInputRef = useRef(null);

  const fetchImages = async () => {
    if (!orderId) return;
    try {
      const res = await api.get(`/api/work-orders/${orderId}/images`);
      setImages(res.data);
    } catch {
      setImages([]);
    }
  };

  useEffect(() => { fetchImages(); }, [orderId]);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const allowedTypes = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Nur Bilder (JPEG, PNG, WebP, HEIC) erlaubt");
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      onRequestUpload?.({ file, url: e.target.result }, () => {
        // Reset input so same file can be re-selected next time
        if (fileInputRef.current) fileInputRef.current.value = "";
        fetchImages();
      });
    };
    reader.readAsDataURL(file);
  };

  const handleDelete = async (imageId) => {
    if (!window.confirm("Bild wirklich löschen?")) return;
    try {
      await api.delete(`/api/work-orders/${orderId}/images/${imageId}`);
      setImages(prev => prev.filter(img => img.id !== imageId));
      toast.success("Bild gelöscht");
    } catch {
      toast.error("Fehler beim Löschen");
    }
  };

  const openFileDialog = (captureType = null) => {
    if (!fileInputRef.current) return;
    if (captureType) fileInputRef.current.setAttribute("capture", captureType);
    else fileInputRef.current.removeAttribute("capture");
    fileInputRef.current.click();
  };

  return (
    <Card data-testid="detail-images-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-purple-600" />Fotos ({images.length})
          </CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => openFileDialog("environment")} className="gap-1" data-testid="detail-camera-btn">
              <Camera className="w-4 h-4" /><span className="hidden sm:inline">Kamera</span>
            </Button>
            <Button size="sm" onClick={() => openFileDialog()} className="gap-1" data-testid="detail-upload-btn">
              <Upload className="w-4 h-4" /><span className="hidden sm:inline">Hochladen</span>
            </Button>
          </div>
        </div>
        <input
          type="file" ref={fileInputRef} onChange={handleFileSelect}
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden"
        />
      </CardHeader>
      <CardContent>
        {images.length > 0 ? (
          <div className="space-y-4">
            {IMAGE_CATEGORIES.map(category => {
              const categoryImages = images.filter(img => img.category === category.id);
              if (categoryImages.length === 0) return null;
              return (
                <div key={category.id}>
                  <Label className="text-xs text-muted-foreground uppercase tracking-wider">
                    {category.label} ({categoryImages.length})
                  </Label>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                    {categoryImages.map((img) => {
                      const imageUrl = img.url?.startsWith('http')
                        ? img.url
                        : `${process.env.REACT_APP_BACKEND_URL}${img.url}`;
                      return (
                        <div key={img.id} className="relative group aspect-square">
                          <img
                            src={imageUrl} alt={img.description || img.original_filename}
                            className="w-full h-full object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => window.open(imageUrl, '_blank')}
                          />
                          <Button
                            variant="destructive" size="icon"
                            className="absolute top-1 right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => { e.stopPropagation(); handleDelete(img.id); }}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                          {img.description && (
                            <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-1 rounded-b-lg truncate">
                              {img.description}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <Camera className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Keine Fotos vorhanden</p>
            <p className="text-sm mt-1">Fügen Sie Fotos zur Dokumentation hinzu</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
